/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { pageViewModifiers, lhpFeatures, PDF_ACTIONS } from "constants/enums";
import { concat } from "utils/lodashUtils";
import * as stores from "stores";
import { transformPDFToView } from "dc-preview-utils/src/Utils/PageUtils";
import { trackTriggeredNonJSAction, trackJSActionsData } from "analytics/events";
import { getJSActionsAnalyticsData, getNeedAppearancesStatus, getTotalPages } from "selectors/documentSelectors";
import { ZOOM_FACTOR } from "constants/constants";
import { showUnsupportedWidgetNotification } from "utils/BannerUtils";
import { getPageHeight } from "selectors/pageSelectors";

export const getInterpretedActions = (pdfActions, extraParams) => {
  let parsedPDFActions = pdfActions;
  if (pdfActions && typeof pdfActions.valueOf() === "string") {
    try {
      parsedPDFActions = JSON.parse(pdfActions);
    } catch (e) {
      return [];
    }
  }
  return interpretActions(parsedPDFActions, extraParams);
};

const getWindowWidth = () => window.innerWidth;

const getWindowHeight = () => window.innerHeight;

const getFitRZoomLevel = (rectWidth, rectHeight) => {
  if (rectWidth > 0 && rectHeight > 0) {
    const zoomReqdForWidth = getWindowWidth() / rectWidth;
    const zoomReqdForHeight = getWindowHeight() / rectHeight;
    return Math.min(zoomReqdForHeight, zoomReqdForWidth);
  }
  return -1;
};

const getZoomAction = destDict => {
  const zoomAction = {};
  let zoomLevel;
  switch (destDict.subtype) {
    case "XYZ":
      if (destDict.zoom > 0) {
        zoomAction.command = "ChangeZoomLevel";
        zoomAction.zoomLevel = destDict.zoom * ZOOM_FACTOR;
      }
      break;
    // These are equivalent to fitpage
    case "Fit":
    case "FitB":
      zoomAction.command = "ChangePageViewModifier";
      zoomAction.pageViewModifier = pageViewModifiers.FIT_PAGE;
      break;
    // FitH stands for fit the page horizontally
    case "FitH":
    case "FitBH":
      zoomAction.command = "ChangePageViewModifier";
      zoomAction.pageViewModifier = pageViewModifiers.FIT_WIDTH;
      break;
    // FitV stands for fit the page Vertically
    case "FitV":
    case "FitBV":
      zoomAction.command = "ChangePageViewModifier";
      zoomAction.pageViewModifier = pageViewModifiers.FIT_HEIGHT;
      break;
    // Fit a Rectangle to page view
    case "FitR":
      const { top, left, bottom, right } = destDict;
      const width = right - left;
      const height = top - bottom;
      zoomLevel = getFitRZoomLevel(width, height);
      if (zoomLevel > 0) {
        zoomAction.command = "ChangeZoomLevel";
        zoomAction.zoomLevel = zoomLevel;
      }
      break;
    default:
      console.log("Unknown Destination Subtype", destDict.subtype);
      break;
  }
  return zoomAction;
};

const getGoToLocationAction = destDict => {
  const { left, top, pageNum } = destDict;

  const newPosition = transformPDFToView(left || 0, top || 0, pageNum, stores, 1);
  newPosition.x = left === undefined ? 0 : newPosition.x;
  newPosition.y = top === undefined ? 0 : newPosition.y;

  const pageHeight = getPageHeight(stores, pageNum);
  const totalPages = getTotalPages(stores);
  if (Math.abs(top) > pageHeight * (totalPages - pageNum - 1)) {
    newPosition.y = 0;
  }
  return {
    command: "GoToLocation",
    pageNum,
    ...newPosition
  };
};

const interpretGoToAction = actionData => {
  const interpretedActions = [];
  if (actionData) {
    const zoomAction = getZoomAction(actionData);
    if (Object.keys(zoomAction).length !== 0) {
      interpretedActions.push(zoomAction);
    }
    interpretedActions.push(getGoToLocationAction(actionData));
  }
  return interpretedActions;
};

const interpretURIAction = actionData => {
  const interpretedActions = [];
  const url = `${actionData.base ? actionData.base : ""}${actionData.URI}`;
  if (url) {
    interpretedActions.push({
      command: "GoToURL",
      url,
      isMap: actionData.isMap
    });
  }
  return interpretedActions;
};

const interpretHideAction = actionData => {
  const interpretedActions = [];
  interpretedActions.push({
    command: "Hide",
    actionData
  });
  return interpretedActions;
};

const interpretJSAction = actionData => {
  const interpretedActions = [];
  if (actionData.JS) {
    interpretedActions.push({
      command: "JavaScript",
      jsScript: actionData.JS
    });
  }
  return interpretedActions;
};

const interpretResetFormAction = actionData => {
  const interpretedActions = [];
  interpretedActions.push({
    command: "ResetForm",
    actionData
  });
  return interpretedActions;
};

const interpretSubmitFormAction = actionData => {
  const interpretedActions = [];
  interpretedActions.push({
    command: "SubmitForm",
    actionData
  });
  return interpretedActions;
};

const interpretNamedAction = actionData => {
  const interpretedActions = [];
  let isSupportedAction = true;

  switch (actionData.name) {
    case PDF_ACTIONS.NAMED_ACTIONS.LAST_PAGE:
      interpretedActions.push({
        command: "GoToPage",
        pageName: "LastPage"
      });
      break;

    case PDF_ACTIONS.NAMED_ACTIONS.FIRST_PAGE:
      interpretedActions.push({
        command: "GoToPage",
        pageName: "FirstPage"
      });
      break;

    case PDF_ACTIONS.NAMED_ACTIONS.NEXT_PAGE:
      interpretedActions.push({
        command: "GoToPage",
        pageName: "NextPage"
      });
      break;

    case PDF_ACTIONS.NAMED_ACTIONS.PREV_PAGE:
      interpretedActions.push({
        command: "GoToPage",
        pageName: "PrevPage"
      });
      break;

    case PDF_ACTIONS.NAMED_ACTIONS.SHOW_HIDE_THUMBNAILS:
      interpretedActions.push({
        command: "UpdateSideBarPlugin",
        plugin: lhpFeatures.THUMBNAIL
      });
      break;

    case PDF_ACTIONS.NAMED_ACTIONS.SHOW_HIDE_BOOKMARKS:
      interpretedActions.push({
        command: "UpdateSideBarPlugin",
        plugin: lhpFeatures.BOOKMARKS
      });
      break;

    case PDF_ACTIONS.NAMED_ACTIONS.SHOW_HIDE_FILE_ATTACHMENT:
      interpretedActions.push({
        command: "UpdateSideBarPlugin",
        plugin: lhpFeatures.ATTACHMENTS
      });
      break;

    case PDF_ACTIONS.NAMED_ACTIONS.SAVE_AS:
      interpretedActions.push({
        command: "SavePDF"
      });
      break;

    case PDF_ACTIONS.NAMED_ACTIONS.ZOOM_VIEW_IN:
      interpretedActions.push({
        command: "ZoomIn"
      });
      break;

    case PDF_ACTIONS.NAMED_ACTIONS.ZOOM_VIEW_OUT:
      interpretedActions.push({
        command: "ZoomOut"
      });
      break;

    case PDF_ACTIONS.NAMED_ACTIONS.FIT_WIDTH:
      interpretedActions.push({
        command: "ChangePageViewModifier",
        pageViewModifier: pageViewModifiers.FIT_WIDTH
      });
      break;

    case PDF_ACTIONS.NAMED_ACTIONS.FIT_HEIGHT:
      interpretedActions.push({
        command: "ChangePageViewModifier",
        pageViewModifier: pageViewModifiers.FIT_HEIGHT
      });
      break;

    case PDF_ACTIONS.NAMED_ACTIONS.PRINT:
      interpretedActions.push({
        command: "Print"
      });
      break;

    default:
      isSupportedAction = false;
      console.log("Found unknown Named action", actionData);
      break;
  }
  trackTriggeredNonJSAction(
    {
      actionType: PDF_ACTIONS.NAMED,
      actionSubType: actionData.name
    },
    isSupportedAction
  );
  return interpretedActions;
};

const interpretActions = (actions, extraParams) => {
  if (!actions) {
    return [];
  }

  let interpretedActions = [];
  for (let i = 0; i < actions.length; i++) {
    let isNonJSAction = true,
      isSupportedAction = true;
    const { actionType, actionData } = actions[i];
    switch (actionType) {
      case PDF_ACTIONS.GOTO:
        const goToActions = interpretGoToAction(actionData);
        interpretedActions = concat(interpretedActions, goToActions);
        break;

      case PDF_ACTIONS.URI:
        const uriActions = interpretURIAction(actionData);
        interpretedActions = concat(interpretedActions, uriActions);
        break;

      case PDF_ACTIONS.HIDE:
        const hideActions = interpretHideAction(actionData);
        interpretedActions = concat(interpretedActions, hideActions);
        break;

      case PDF_ACTIONS.JAVASCRIPT:
        const jsAction = interpretJSAction(actionData);
        interpretedActions = concat(interpretedActions, jsAction);
        isNonJSAction = false;
        break;

      case PDF_ACTIONS.RESET_FORM:
        const resetFormActions = interpretResetFormAction(actionData);
        interpretedActions = concat(interpretedActions, resetFormActions);
        break;

      case PDF_ACTIONS.SUBMIT_FORM:
        const submitFormActions = interpretSubmitFormAction(actionData);
        interpretedActions = concat(interpretedActions, submitFormActions);
        break;

      case PDF_ACTIONS.NAMED:
        const namedActions = interpretNamedAction(actionData);
        interpretedActions = concat(interpretedActions, namedActions);
        break;

      default:
        console.log("Found an unknown action!", actionType);
        isSupportedAction = false;
        handleUnsupportedAction(actions[i], extraParams);
        break;
    }
    isNonJSAction && trackTriggeredNonJSAction({ actionType }, isSupportedAction);
  }
  return interpretedActions;
};

export const trackDocAndFormJSActions = (
  formsJSActionsData = { hasFieldJS: false, hasValidateJS: false, hasCalculateJS: false, hasFormatJS: false }
) => {
  const jsActionsData = {
    ...getJSActionsAnalyticsData(stores),
    hasNeedAppearance: getNeedAppearancesStatus(stores),
    ...formsJSActionsData
  };
  trackJSActionsData(jsActionsData);
};

export const handleUnsupportedAction = (action, extraParams) => {
  const data = {
    hasXFA: false,
    hasJS: false,
    hasUnsupportedWidget: false,
    hasUnsupportedActions: true
  };
  if (extraParams && extraParams.triggeredFrom === "acroform") {
    showUnsupportedWidgetNotification(data);
  }
};

const isActionPresent = (actions, event) => !!(actions && actions[event] && actions[event].length);

export const getDocLevelJsTriggerPoints = data => {
  const actions = data.actions;
  const jsTriggerPoints = {
    open: isActionPresent(actions, "Open"),
    didPrint: isActionPresent(actions, "DidPrint"),
    didSave: isActionPresent(actions, "DidSave"),
    willClose: isActionPresent(actions, "WillClose"),
    willPrint: isActionPresent(actions, "WillPrint"),
    willSave: isActionPresent(actions, "WillSave")
  };
  return jsTriggerPoints;
};
